/**
 * The page's COLD-START back target, published by the shell to whoever draws
 * the control.
 *
 * ## The defect this closes (§2.6)
 *
 * `ChromeSpec.mobileHeader` used to be a boolean question — does the SHELL draw
 * a band? — and the browse canvas answered "no", because it draws its own
 * `ListingsTopBar`. So `canvas` sat outside `CHROME_KINDS_WITH_BACK_TARGET`,
 * `webroutesgen` REFUSED a `back:` row on it, and the bar's back control was
 * handed `routes.home()` by hand inside the feature file: the one back
 * destination in the app that no manifest declared, no generator checked and no
 * gate could see. "The generator correctly refuses it" was true and was the
 * problem.
 *
 * The column now says WHO draws the control (`back` = the shell's band, `page` =
 * the page's own bar), and both arms owe a declared target. `PageShell` resolves
 * it once — the same `handle.backTo` / loader `backHref` resolution the band
 * uses, with the same `internalPath` refusal of a value that is not a same-app
 * path — and publishes it here.
 *
 * ## Why it lives in `providers/` and not beside the shell
 *
 * Because `ListingsTopBar` is a FEATURE, and `feature → infra` is refused by the
 * app's DAG (`@letsgethome/element-types`): "a feature component may not compose
 * page chrome". A provider in `shared/components/layout/` would have been
 * `infra` and unreachable from the one component that needs it — the same
 * boundary `BackLink.tsx`'s own header records. `src/shared/providers/**` is the
 * `hook` layer for exactly this reason, in its own words: *"context providers
 * expose cross-cutting hooks consumed by every layer, so they belong to the
 * all-importable `hook` layer rather than `infra`"*.
 *
 * ## Why a context of ours and not `<Outlet context>`
 *
 * React Router's `useOutlet(context)` wraps the child tree in
 * `OutletContext.Provider value={context}` on EVERY `<Outlet />`, so a bare
 * `<Outlet />` in any intermediate group layout resets the value to `undefined`
 * — silently, at run time, for the one page downstream of it. Today no such
 * layout sits between the shell and the canvas; "today" is not a contract.
 */
import { createSafeContext } from '@letsgethome/lib/react/create-safe-context';
import type { Route } from '@letsgethome/routes';

/**
 * BOXED, and that is the whole reason this is an object and not a `Route | null`.
 *
 * `createSafeContext`'s strict mode uses `null` as its own "no Provider above
 * me" sentinel. A `null` TARGET is a different fact — a shell DID resolve, and
 * resolved nothing, which is what every row that shows no back control answers —
 * and the two failures want different sentences. The box keeps the context value
 * non-null so each is reported as itself.
 */
const [PageBackTargetProvider, usePageBackTargetBox] = createSafeContext<{
  readonly target: Route | null;
}>('PageBackTarget');

export { PageBackTargetProvider };

/**
 * The declared back target for the surrounding page.
 *
 * THROWS rather than defaulting, in both failure modes, for the reason
 * `pageChromeFromMatches` throws: a back control that quietly points at "home"
 * is indistinguishable from a correct one until a reader presses it, which is
 * precisely how the canvas's target went four rounds with no gate.
 *
 * Unconditional, not development-only — a dev-only throw reproduces the failure
 * mode it closes (the `SlideCarousel` precedent).
 */
export function usePageBackTarget(): Route {
  const { target } = usePageBackTargetBox();
  if (target === null) {
    throw new Error(
      "usePageBackTarget: this page's chrome resolved no back target. A page that draws its own back control must resolve to a chrome kind in CHROME_KINDS_WITH_BACK_TARGET, which webroutesgen then REQUIRES a `back:` route id from."
    );
  }
  return target;
}
